import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { scalarTokens } from "@outpave/ds";
import type { ColorScheme } from "context/ColorSchemeContext";
import { Stack, Inline } from "components/layout/reusableLayouts";

/** The dashboard in the draft's colours, painted from resolved tokens: `var()` and real components would show the live theme. */

interface Props {
  /** The draft's resolved tokens for `scheme`, under the draft's theme. */
  tokens: Record<string, string>;
  scheme: ColorScheme;
  onSchemeChange: (scheme: ColorScheme) => void;
  logoUrl?: string;
  lightLogoUrl?: string;
}

const W = 640;
const H = 400;
const CHROME = 28;
const RAIL = 104;
const PAD = 14;
const GAP = 10;
const MAIN_X = RAIL + PAD;
const MAIN_W = W - MAIN_X - PAD;
const ROW_1 = { y: CHROME + PAD, h: 44 };
const ROW_2 = { y: ROW_1.y + ROW_1.h + GAP, h: 146 };
const ROW_3 = { y: ROW_2.y + ROW_2.h + GAP, h: 134 };
// Full-size corner radii read as circles at this scale, so they are capped.
const MAX_RADIUS = 8;
const SHADOW_OFFSET = 5;

const STATS = [
  { tone: "success", value: "$23.4K" },
  { tone: "warning", value: "$14.2K" },
  { tone: "danger", value: "$4.3K" },
] as const;
const NAV_WIDTHS = [36, 44, 30, 40, 34];
const SHARES = [0.26, 0.19, 0.15, 0.12, 0.1, 0.08, 0.06, 0.04];
const BAR_PAIRS = [
  [40, 60],
  [52, 74],
  [26, 43],
  [57, 83],
];
const AREA =
  "M0,28 C14,26 22,16 34,18 C46,20 52,9 64,11 C76,13 84,19 98,12 C106,8 114,7 120,9";

/** Splits the given widths across `total`, with `GAP` between them. */
const split = (x: number, total: number, weights: number[]) => {
  const unit =
    (total - GAP * (weights.length - 1)) / weights.reduce((a, b) => a + b, 0);
  let at = x;
  return weights.map((weight) => {
    const cell = { x: at, w: weight * unit };
    at += cell.w + GAP;
    return cell;
  });
};

const px = (value?: string) => {
  const match = value?.match(/^(-?\d*\.?\d+)px$/);
  return match ? Number(match[1]) : undefined;
};

const SCALAR: Record<string, string> = scalarTokens;

/** A theme's values can be CSS expressions over other tokens, so this fills in their `var()`s from the draft. */
export const resolveVars = (
  value: string | undefined,
  tokens: Record<string, string>,
  depth = 0,
): string | undefined => {
  if (!value || depth > 8 || !value.includes("var(")) return value;
  const next = value.replace(
    /var\(--([\w-]+)(?:,\s*([^()]*))?\)/g,
    (_, name: string, fallback?: string) =>
      tokens[name] ?? SCALAR[name] ?? fallback ?? "",
  );
  return next === value ? value : resolveVars(next, tokens, depth + 1);
};

const BrandingLivePreview: React.FC<Props> = ({
  tokens,
  scheme,
  onSchemeChange,
  logoUrl,
  lightLogoUrl,
}) => {
  const { t } = useTranslation();
  const svgId = useId().replace(/[^\w-]/g, "");
  const token = (name: string) => tokens[name];
  const resolved = (name: string) => resolveVars(tokens[name], tokens);
  const logo = scheme === "dark" ? lightLogoUrl : logoUrl;

  // What the theme changes besides colour: corners, type, outlines, shadows, and Phosphor's cells.
  const radius = Math.min(px(token("radius-md")) ?? MAX_RADIUS, MAX_RADIUS);
  const pill = (height: number) =>
    Math.min(px(token("radius-pill")) ?? height / 2, height / 2);
  const outlined = Boolean(token("neo-border"));
  const outline = outlined
    ? resolveVars("var(--neo-ink)", tokens)
    : token("color-border");
  const shadow = token("neo-shadow")
    ? resolveVars("var(--neo-shadow-color)", tokens)
    : undefined;
  const panel = resolved("neo-panel-bg") ?? token("color-bg-elevated");
  const nav = resolved("neo-nav-bg") ?? token("color-bg-surface-canvas");
  const cell = px(token("phosphor-cell"));
  const cellGap = px(token("phosphor-gap")) ?? 0;

  const tile = (x: number, y: number, w: number, h: number) => (
    <>
      {shadow && (
        <rect
          x={x + SHADOW_OFFSET}
          y={y + SHADOW_OFFSET}
          width={w}
          height={h}
          rx={radius}
          style={{ fill: shadow }}
        />
      )}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={radius}
        strokeWidth={outlined ? 2 : 1}
        style={{ fill: panel, stroke: outline }}
      />
    </>
  );

  const bar = (x: number, y: number, w: number, color: string, h = 4) => (
    <rect x={x} y={y} width={w} height={h} rx={pill(h)} fill={color} />
  );

  // A status chip in miniature: the container colour with its ink on it.
  const chip = (x: number, y: number, tone: string) => (
    <g>
      <rect
        x={x}
        y={y}
        width={30}
        height={12}
        rx={pill(12)}
        fill={token(`color-status-${tone}-bg`)}
      />
      <circle
        cx={x + 7}
        cy={y + 6}
        r={2.5}
        fill={token(`color-status-${tone}-text`)}
      />
      {bar(x + 13, y + 4.5, 11, token(`color-status-${tone}-text`), 3)}
    </g>
  );

  const stats = split(MAIN_X, MAIN_W, [1, 1, 1]);
  const [chart, accounts] = split(MAIN_X, MAIN_W, [1.7, 1]);
  const [donut, list, bars] = split(MAIN_X, MAIN_W, [1, 1.15, 1]);

  const donutR = 30;
  const circumference = 2 * Math.PI * donutR;
  // Eight series, so slots 4 to 8 (the strategy's companions) show as well as the brand three.
  const slices = SHARES.map((share, i) => ({
    color: token(`color-chart-${i + 1}`),
    share,
  }));
  const donutCx = donut.x + 44;
  const donutCy = ROW_3.y + ROW_3.h / 2;

  const barsInnerW = bars.w - 24;
  const barsInnerH = ROW_3.h - 24;
  const pairW = barsInnerW / BAR_PAIRS.length;
  const barW = pairW * 0.36;

  return (
    <Stack gap="var(--space-3)">
      <Inline justify="between" align="center" wrap={false}>
        <Typography variant="h6">{t("Live preview")}</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scheme}
          onChange={(_, next) => next && onSchemeChange(next as ColorScheme)}
          aria-label={t("Preview mode")}
        >
          <ToggleButton value="light">{t("Light")}</ToggleButton>
          <ToggleButton value="dark">{t("Dark")}</ToggleButton>
        </ToggleButtonGroup>
      </Inline>

      {/* A fixed light mat, so the picture keeps its edge on any saved theme. */}
      <Box
        sx={{
          padding: "var(--space-3)",
          borderRadius: "var(--radius-lg)",
          backgroundColor: "var(--color-bg-preview-light)",
        }}
      >
        <Box
          sx={{
            overflow: "hidden",
            borderRadius: "var(--radius-lg)",
            border: `var(--border-width-1) solid ${token("color-border")}`,
            lineHeight: 0,
          }}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={t("BrandingPreviewLabel")}
            style={{ display: "block", width: "100%", height: "auto" }}
          >
            {cell && (
              <defs>
                <pattern
                  id={`${svgId}-cells`}
                  width={cell + cellGap}
                  height={cell + cellGap}
                  patternUnits="userSpaceOnUse"
                >
                  <rect width={cell} height={cell} fill="white" />
                </pattern>
                <mask id={`${svgId}-mask`}>
                  <rect width={W} height={H} fill={`url(#${svgId}-cells)`} />
                </mask>
              </defs>
            )}

            <rect width={W} height={H} fill={token("color-bg-page")} />

            {/* The macOS traffic lights are a fixed window metaphor, not a
                product surface, so they stay literal in both schemes. */}
            {["#FF5F57", "#FEBC2E", "#28C840"].map((dot, i) => (
              <circle key={dot} cx={16 + i * 13} cy={14} r={4} fill={dot} />
            ))}
            {bar(W / 2 - 56, 9, 112, token("color-bg-elevated"), 10)}

            <rect
              x={0}
              y={CHROME}
              width={RAIL}
              height={H - CHROME}
              style={{ fill: nav }}
            />
            {logo ? (
              <image
                href={logo}
                x={14}
                y={CHROME + 12}
                width={72}
                height={20}
                preserveAspectRatio="xMinYMid meet"
              />
            ) : (
              bar(14, CHROME + 17, 52, token("color-bg-elevated"), 10)
            )}
            {NAV_WIDTHS.map((w, i) => {
              const y = CHROME + 48 + i * 30;
              const selected = i === 1;
              return (
                <g key={i}>
                  {selected && (
                    <rect
                      x={8}
                      y={y}
                      width={RAIL - 16}
                      height={24}
                      rx={radius}
                      fill={token("color-bg-elevated")}
                    />
                  )}
                  {bar(
                    16,
                    y + 10,
                    w * 1.3,
                    token(selected ? "color-border-strong" : "color-border"),
                    5,
                  )}
                </g>
              );
            })}

            {stats.map(({ x, w }, i) => (
              <g key={STATS[i].tone}>
                {tile(x, ROW_1.y, w, ROW_1.h)}
                {bar(x + 12, ROW_1.y + 9, 40, token("color-border-strong"), 5)}
                <text
                  data-testid={i === 0 ? "preview-stat-value" : undefined}
                  x={x + 12}
                  y={ROW_1.y + 34}
                  fontSize={14}
                  style={{
                    fill: token("color-text-primary"),
                    fontFamily: token("font-family-display"),
                    fontWeight: token("neo-display-weight") ?? 600,
                  }}
                >
                  {STATS[i].value}
                </text>
                {chip(x + w - 42, ROW_1.y + 10, STATS[i].tone)}
              </g>
            ))}

            {tile(chart.x, ROW_2.y, chart.w, ROW_2.h)}
            <svg
              x={chart.x + 12}
              y={ROW_2.y + 16}
              width={chart.w - 24}
              height={ROW_2.h - 28}
              viewBox="0 0 120 36"
              preserveAspectRatio="none"
            >
              <path
                d={`${AREA} L120,36 L0,36 Z`}
                fill={token("color-chart-1")}
                fillOpacity={0.14}
              />
              <path
                d={AREA}
                fill="none"
                stroke={token("color-chart-1")}
                strokeWidth={px(token("chart-line-width")) ?? 1.5}
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {tile(accounts.x, ROW_2.y, accounts.w, ROW_2.h)}
            <rect
              x={accounts.x + 10}
              y={ROW_2.y + 10}
              width={accounts.w - 20}
              height={80}
              rx={radius}
              fill={token("color-bg-page")}
            />
            {[0, 1].map((i) => {
              const y = ROW_2.y + 16 + i * 36;
              const x = accounts.x + 16;
              const w = accounts.w - 32;
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={30}
                    rx={Math.min(radius, 5)}
                    fill={token("color-bg-surface")}
                  />
                  <rect
                    x={x + 7}
                    y={y + 8}
                    width={14}
                    height={14}
                    rx={Math.min(radius, 3)}
                    fill={token("color-border-strong")}
                  />
                  {bar(x + 28, y + 9, w - 36, token("color-border-strong"))}
                  {bar(x + 28, y + 18, 40, token("color-border"))}
                </g>
              );
            })}
            <g>
              <rect
                data-testid="preview-primary-button"
                x={accounts.x + accounts.w - 64}
                y={ROW_2.y + ROW_2.h - 32}
                width={52}
                height={20}
                rx={pill(20)}
                fill={token("color-bg-brand")}
              />
              {bar(
                accounts.x + accounts.w - 52,
                ROW_2.y + ROW_2.h - 24,
                28,
                token("color-text-on-brand"),
              )}
            </g>

            {tile(donut.x, ROW_3.y, donut.w, ROW_3.h)}
            {slices.map(({ color, share }, i) => {
              const before = slices
                .slice(0, i)
                .reduce((sum, slice) => sum + slice.share, 0);
              return (
                <circle
                  key={i}
                  cx={donutCx}
                  cy={donutCy}
                  r={donutR}
                  fill="none"
                  stroke={color}
                  strokeWidth={9}
                  strokeDasharray={`${share * circumference} ${circumference}`}
                  strokeDashoffset={-before * circumference}
                  transform={`rotate(-90 ${donutCx} ${donutCy})`}
                />
              );
            })}
            {slices.map(({ color }, i) => (
              <g key={i}>
                <circle
                  cx={donut.x + 90 + Math.floor(i / 4) * 30}
                  cy={donutCy - 21 + (i % 4) * 14}
                  r={3}
                  fill={color}
                />
                {bar(
                  donut.x + 96 + Math.floor(i / 4) * 30,
                  donutCy - 23 + (i % 4) * 14,
                  16,
                  token("color-border-strong"),
                )}
              </g>
            ))}

            {[0, 1, 2].map((i) => {
              const h = (ROW_3.h - 12) / 3;
              const y = ROW_3.y + i * (h + 6);
              return (
                <g key={i}>
                  {tile(list.x, y, list.w, h)}
                  <rect
                    x={list.x + 8}
                    y={y + h / 2 - 7}
                    width={14}
                    height={14}
                    rx={Math.min(radius, 3)}
                    fill={token("color-border-strong")}
                  />
                  {bar(
                    list.x + 28,
                    y + h / 2 - 6,
                    64,
                    token("color-border-strong"),
                  )}
                  {bar(list.x + 28, y + h / 2 + 3, 40, token("color-border"))}
                  {i === 0 && chip(list.x + list.w - 40, y + h / 2 - 6, "info")}
                </g>
              );
            })}

            {tile(bars.x, ROW_3.y, bars.w, ROW_3.h)}
            <g mask={cell ? `url(#${svgId}-mask)` : undefined}>
              {BAR_PAIRS.map(([a, b], i) => {
                const centre = bars.x + 12 + pairW * i + pairW / 2;
                const floor = ROW_3.y + ROW_3.h - 12;
                return (
                  <g key={i}>
                    <rect
                      x={centre - barW - 1}
                      y={floor - (barsInnerH * a) / 100}
                      width={barW}
                      height={(barsInnerH * a) / 100}
                      rx={Math.min(radius, 3)}
                      fill={token("color-chart-2")}
                    />
                    <rect
                      x={centre + 1}
                      y={floor - (barsInnerH * b) / 100}
                      width={barW}
                      height={(barsInnerH * b) / 100}
                      rx={Math.min(radius, 3)}
                      fill={token("color-chart-1")}
                    />
                  </g>
                );
              })}
            </g>
          </svg>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "var(--color-text-tertiary)",
          textAlign: "center",
        }}
      >
        {t("BrandingPreviewCaption")}
      </Typography>
    </Stack>
  );
};

export default BrandingLivePreview;
